import { forwardRef } from 'react'

function mergeClasses(...classes) {
  return classes.filter(Boolean).join(' ')
}

const Pagination = ({ className = '', children, ...props }) => (
  <nav
    role="navigation"
    aria-label="pagination"
    className={mergeClasses('mx-auto w-full', className)}
    {...props}
  >
    {children}
  </nav>
)

const PaginationContent = forwardRef(function PaginationContent(
  { className = '', children, ...props },
  ref,
) {
  return (
    <ul
      ref={ref}
      className={mergeClasses('flex flex-row items-center gap-1.5', className)}
      {...props}
    >
      {children}
    </ul>
  )
})

const PaginationItem = forwardRef(function PaginationItem(
  { className = '', children, ...props },
  ref,
) {
  return (
    <li ref={ref} className={mergeClasses('', className)} {...props}>
      {children}
    </li>
  )
})

const PaginationLink = forwardRef(function PaginationLink(
  { className = '', isActive = false, disabled = false, children, ...props },
  ref,
) {
  return (
    <button
      ref={ref}
      type="button"
      aria-current={isActive ? 'page' : undefined}
      disabled={disabled}
      className={mergeClasses(
        'inline-flex h-9 min-w-9 items-center justify-center rounded-full border px-2.5 text-sm font-semibold transition-colors',
        isActive
          ? 'border-primary bg-primary text-on-primary shadow-sm'
          : 'border-outline-variant bg-surface-container-lowest text-on-surface hover:border-primary/40 hover:bg-primary hover:text-on-primary',
        'disabled:cursor-not-allowed disabled:opacity-50',
        className,
      )}
      {...props}
    >
      {children}
    </button>
  )
})

const PaginationPrevious = forwardRef(function PaginationPrevious(
  { className = '', children, ...props },
  ref,
) {
  return (
    <PaginationLink ref={ref} aria-label="Go to previous page" className={className} {...props}>
      {children || <span className="material-symbols-outlined text-lg leading-none">chevron_left</span>}
    </PaginationLink>
  )
})

const PaginationNext = forwardRef(function PaginationNext(
  { className = '', children, ...props },
  ref,
) {
  return (
    <PaginationLink ref={ref} aria-label="Go to next page" className={className} {...props}>
      {children || <span className="material-symbols-outlined text-lg leading-none">chevron_right</span>}
    </PaginationLink>
  )
})

const PaginationEllipsis = ({ className = '', ...props }) => (
  <span
    aria-hidden
    className={mergeClasses('inline-flex h-9 min-w-9 items-center justify-center text-on-surface-variant', className)}
    {...props}
  >
    <span className="material-symbols-outlined text-base leading-none">more_horiz</span>
  </span>
)

export {
  Pagination,
  PaginationContent,
  PaginationItem,
  PaginationLink,
  PaginationPrevious,
  PaginationNext,
  PaginationEllipsis,
}
